import React, {memo, Suspense} from 'react';
import {useAppSelector, useTranslation} from '../hooks';

import {ProgressSpinner} from 'primereact/progressspinner';
import {createPortal} from 'react-dom';
import {ErrorBoundary} from 'react-error-boundary';
import {Navigate, Route, Routes} from 'react-router-dom';

import AppMenu from '../components/AppMenu';
import AppSettings from '../components/AppSettings';
import Footer from '../components/Footer';
import NoMatch from '../components/NoMatch';
import WhatsApp from '../components/WhatsApp';
import localization from '../locale/localization.json';
import {LanguageOptions, languageState} from '../redux/slices/app';
import {Localization} from '../types';
import {LANGS} from '../utils';
import Home from './home';

export const PATHS = [
  'pathMenuHome',
  'pathMenuAbout',
  'pathMenuProductsTitle',
  'pathMenuProductsSubTitle1',
  'pathMenuProductsSubTitle1Option1',
  'pathMenuProductsSubTitle1Option2',
  'pathMenuProductsSubTitle1Option3',
  'pathMenuProductsSubTitle1Option4',
  'pathMenuProductsSubTitle1Option5',
  'pathMenuProductsSubTitle1Option6',
  'pathMenuProductsSubTitle1Option7',
  'pathMenuProductsSubTitle1Option8',
  'pathMenuProductsSubTitle1Option9',
  'pathMenuProductsSubTitle2',
  'pathMenuProductsSubTitle2Option1',
  'pathMenuProductsSubTitle2Option2',
  'pathMenuProductsSubTitle2Option3',
  'pathMenuProductsSubTitle2Option4',
  'pathMenuProductsSubTitle2Option5',
  'pathMenuProductsSubTitle2Option6',
  'pathMenuProductsSubTitle3',
  'pathMenuProductsSubTitle3Option1',
  'pathMenuProductsSubTitle3Option2',
  'pathMenuProductsSubTitle3Option3',
  'pathMenuProductsSubTitle3Option4',
  'pathMenuProductsSubTitle4',
  'pathMenuProductsSubTitle4Option1',
  'pathMenuContact',
];

const Contact = React.lazy(() => import('./contact'));
const About = React.lazy(() => import('../components/About'));
const Category = React.lazy(() => import('./category'));
const Productdetails = React.lazy(() => import('./productdetails'));
const ErrorFallback = React.lazy(() => import('../components/ErrorFallback'));
const NotFound = React.lazy(() => import('../components/NoMatch'));

const Root = () => {
  const {t} = useTranslation();
  const selectedLanguage = useAppSelector(languageState);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="p-align-center min-h-screen px-5 py-3">
        <AppSettings />
        <AppMenu />
        <main>
          <Suspense
            fallback={
              <div className="min-h-screen flex justify-content-center align-items-center">
                <ProgressSpinner className="w-4rem h-4rem align-items-center" />
              </div>
            }>
            <Routes>
              <Route
                path="/"
                element={
                  <Navigate
                    to={`/${selectedLanguage}${t('pathMenuHome')}`}
                    replace
                  />
                }
              />
              {LANGS.map((lang) =>
                getRoutesForLanguage(lang as LanguageOptions).map((r, i) => (
                  <Route
                    key={`${lang}-${i}`}
                    path={r.path}
                    element={
                      <ErrorBoundary FallbackComponent={NotFound}>
                        {r.element}
                      </ErrorBoundary>
                    }
                  />
                )),
              )}
              {/* {routes?.map((r, i) => (
                <Route key={i} path={r.path} element={r.element} />
              ))} */}
              {/* <Route
                path="*"
                element={
                  <Navigate
                    to={`/${selectedLanguage}/${t('pathMenuHome')}`}
                    replace
                  />
                }
              /> */}
              <Route path="*" element={<NoMatch />} />
            </Routes>
          </Suspense>
        </main>
        {document.getElementById('whatsapp') &&
          createPortal(<WhatsApp />, document.getElementById('whatsapp')!)}
      </div>
      <Footer />
    </ErrorBoundary>
  );
};

export default memo(Root);

interface AppRoute {
  path: string;
  element: React.ReactNode;
}

export const extractRoutes = (lang: LanguageOptions, t: any) => {
  const routes: AppRoute[] = [];
  PATHS.forEach((k) => {
    routes.push({
      path: '/' + lang + t(k),
      element: setElements(k),
    });
  });
  return routes;
};

const setElements = (key: string) => {
  let element: React.ReactNode;
  switch (true) {
    case key.includes('Home'):
      element = <Home />;
      break;
    case key.includes('About'):
      element = <About />;
      break;
    case key.includes('Option'):
      element = <Productdetails />;
      break;
    case key.includes('SubTitle'):
      element = <Category />;
      break;
    case key.includes('Contact'):
      element = <Contact />;
      break;
    default:
      element = <NotFound />;
  }

  return element;
};

// Pre-generate routes for all languages
const allRoutes: Record<string, AppRoute[]> = LANGS.reduce<
  Record<string, AppRoute[]>
>((acc, lang) => {
  const localizedData = (localization as Localization)[lang];

  acc[lang] = Object.entries(localizedData)
    .filter(([key]) => key.startsWith('path'))
    .map(([key, value]) => ({
      path: `/${lang}${value as string}`,
      element: setElements(key),
    }));

  return acc;
}, {});

export const getRoutesForLanguage = (lang: LanguageOptions): AppRoute[] => {
  return allRoutes[lang] || [];
};
